import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js/pure';
import { CheckoutElementsProvider, PaymentElement, ExpressCheckoutElement, useCheckoutElements } from '@stripe/react-stripe-js/checkout';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { CheckoutErrorBoundary } from './CheckoutErrorBoundary';
import api from '../../lib/api';
import { track } from '../../lib/analytics';
import { CheckoutColumns } from './CheckoutLayout';
import { PaymentPanel, MobilePayBar } from './PaymentPanel';
import { CouponDrawer } from './CouponDrawer';
import { CouponAppliedModal } from './CouponAppliedModal';

const APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#0f172a', colorBackground: '#ffffff', colorText: '#0f172a', colorTextSecondary: '#64748b',
    colorDanger: '#e11d48', fontFamily: 'Manrope, system-ui, sans-serif', fontSizeBase: '15px', borderRadius: '14px',
  },
  rules: {
    '.Input': { border: '1px solid #e2e8f0', boxShadow: 'none', padding: '13px 14px' },
    '.Input:focus': { border: '1px solid #0f172a', boxShadow: '0 0 0 3px rgba(15,23,42,0.08)' },
    '.Label': { fontWeight: '600', fontSize: '13px', color: '#475569', marginBottom: '6px' },
    '.Tab': { border: '1px solid #e2e8f0', boxShadow: 'none' },
    '.Tab--selected': { border: '2px solid #0f172a', backgroundColor: '#0f172a', color: '#ffffff', boxShadow: '0 8px 20px -12px rgba(15,23,42,0.6)' },
    '.Tab--selected:hover': { color: '#ffffff' },
    '.TabIcon--selected': { fill: '#ffffff' },
    '.TabLabel--selected': { color: '#ffffff' },
    '.Block': { border: '1px solid #e2e8f0', boxShadow: 'none' },
  },
};
const FONTS = [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap' }];
// Default (proven on production): Stripe's Payment Element tabs with Apple Pay first — the selected tab is solid black.
// The Express Checkout Element (black Apple Pay / Link buttons above an "OR") is available behind ?express=1 for
// testing on real devices; it also shows an on-screen event log so we can see what Stripe reports.
const PE_WITH_WALLETS = { layout: 'tabs', paymentMethodOrder: ['apple_pay', 'google_pay', 'link', 'card'] };
const PE_CARD_ONLY = { layout: 'tabs', wallets: { applePay: 'never', googlePay: 'never', link: 'never' } };
const expressRequested = () => { try { return new URLSearchParams(window.location.search).get('express') === '1'; } catch { return false; } };
// Deliberately minimal — every key is in @stripe/stripe-js StripeCheckoutExpressCheckoutElementOptions.
// (An option Stripe rejects makes Stripe.js throw synchronously; the boundary below then hides this row.)
const EXPRESS_OPTS = {
  buttonHeight: 48,
  buttonTheme: { applePay: 'black', googlePay: 'black' },
  layout: { maxColumns: 2, maxRows: 1, overflow: 'never' },
  paymentMethodOrder: ['link', 'applePay', 'googlePay'],
};

let stripePromise = null;
const getStripe = (pk) => { if (!stripePromise) stripePromise = loadStripe(pk); return stripePromise; };
const isPublishable = (pk) => /^pk_(live|test)_[A-Za-z0-9]+$/.test(String(pk || ''));

/** Normalise Stripe's session into the quote shape the summary renders. */
export function quoteFromStripe(c) {
  const d = (c.discountAmounts || [])[0];
  return {
    currency: c.currency,
    divisor: c.minorUnitsAmountDivisor || 100,
    subtotal: c.total.subtotal.minorUnitsAmount,
    discount: c.total.discount.minorUnitsAmount,
    total: c.total.total.minorUnitsAmount,
    tax: c.total.taxExclusive?.minorUnitsAmount || 0,
    code: d?.promotionCode || null,
    percentOff: d?.percentOff || null,
  };
}

function FieldsSkeleton() {
  return (
    <div className="space-y-3" data-testid="payment-fields-skeleton">
      <div className="h-12 rounded-2xl bg-slate-100 animate-pulse" />
      <div className="grid grid-cols-2 gap-3"><div className="h-12 rounded-2xl bg-slate-100 animate-pulse" /><div className="h-12 rounded-2xl bg-slate-100 animate-pulse" /></div>
      <div className="h-12 rounded-2xl bg-slate-100 animate-pulse" />
    </div>
  );
}

/** Link | Apple Pay buttons on top, then "OR", then the card form. Hidden when the device offers no wallet. */
function ExpressRow({ cfg, methods, consent, onReady, onAvailable, onConfirm, onLoadError, onGate }) {
  const has = methods && methods.length > 0;
  return (
    <div className={has ? 'mb-5' : 'min-h-[48px]'} data-testid="express-checkout" data-methods={(methods || []).join(',')}>
      <div className="relative">
        <ExpressCheckoutElement options={EXPRESS_OPTS} onReady={onReady} onAvailablePaymentMethodsChange={onAvailable} onConfirm={onConfirm} onLoadError={onLoadError} />
        {has && !consent && (
          <button type="button" onClick={onGate} aria-label="Accept the refund policy to pay with a wallet" data-testid="express-consent-gate"
            className="absolute inset-0 z-10 rounded-2xl bg-transparent cursor-pointer" />
        )}
      </div>
      {has && (
        <div className="mt-5 flex items-center gap-3" data-testid="express-divider">
          <span className="h-px flex-1 bg-slate-200" />
          <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400">{cfg.express_divider_label || 'Or'}</span>
          <span className="h-px flex-1 bg-slate-200" />
        </div>
      )}
    </div>
  );
}

function EmbeddedBody({ cfg, urlCode, email, sessionId, onFallback }) {
  const state = useCheckoutElements();
  const checkout = state.type === 'success' ? state.checkout : null;
  const quote = checkout ? quoteFromStripe(checkout) : null;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [applied, setApplied] = useState(null);
  const [couponErr, setCouponErr] = useState('');
  const [busyCode, setBusyCode] = useState('');
  const [consent, setConsent] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payErr, setPayErr] = useState('');
  const autoRef = useRef(false);

  const applyCode = useCallback(async (code, { fromUrl } = {}) => {
    if (!checkout) return false;
    const c = String(code || '').trim().toUpperCase();
    setBusyCode(c); setCouponErr('');
    let res;
    try { res = await checkout.applyPromotionCode(c); } catch (e) { res = { type: 'error', error: { message: 'Could not apply this code right now — please try again.' } }; }
    setBusyCode('');
    if (res.type === 'error') {
      const isCredit = cfg.credit?.active && c === String(cfg.credit.code || '').toUpperCase();
      const stripeMsg = String(res.error?.message || '').trim();
      setCouponErr(isCredit
        ? `We could not apply your wallet credits just now${stripeMsg ? ` (Stripe said: “${stripeMsg}”)` : ''}. Refresh this page and press "Use" again — if it still fails, message us and we will apply them by hand.`
        : (stripeMsg || cfg.coupon_invalid_text || "That code isn't valid or has expired."));
      if (fromUrl) setDrawerOpen(true);
      track('coupon_failed', { code: c, message: stripeMsg, credit: !!isCredit });
      return false;
    }
    const s = res.session;
    const d = (s.discountAmounts || [])[0];
    const div = s.minorUnitsAmountDivisor || 100;
    setApplied({ code: d?.promotionCode || c, savings: s.total.discount.minorUnitsAmount, total: s.total.total.minorUnitsAmount, currency: s.currency, divisor: div });
    setDrawerOpen(false);
    track('coupon_applied', { code: c, savings: s.total.discount.minorUnitsAmount / div, value: s.total.total.minorUnitsAmount / div, currency: s.currency, mode: 'embedded' });
    return true;
  }, [checkout, cfg.coupon_invalid_text]);

  const autoCode = urlCode || (cfg.credit?.active ? cfg.credit.code : '');
  useEffect(() => {
    if (checkout && autoCode && !autoRef.current) {
      autoRef.current = true;
      if (!quoteFromStripe(checkout).code) applyCode(autoCode, { fromUrl: !!urlCode });
    }
  }, [checkout, autoCode, urlCode, applyCode]);

  const readyTracked = useRef(false);
  useEffect(() => {
    if (checkout && !readyTracked.current) {
      readyTracked.current = true;
      const q = quoteFromStripe(checkout);
      track('checkout_ready', { mode: 'embedded', value: q.total / q.divisor, currency: q.currency });
    }
  }, [checkout]);

  const removeCode = async () => { if (checkout) { try { await checkout.removePromotionCode(); } catch { /* ignore */ } } };
  const onConsent = (v) => { setConsent(v); setPayErr(''); api.post('/api/payments/checkout-consent', { session_id: sessionId, accepted: v }).catch(() => {}); };

  // Express buttons are opt-in (?express=1) until they are proven on real devices. null = waiting for Stripe to say which
  // wallets exist; [] = none / failed / not requested → no express element, card form shows its wallet tabs.
  const expressWanted = useMemo(expressRequested, []);
  const [expressMethods, setExpressMethods] = useState(expressWanted ? null : []);
  const [expressLog, setExpressLog] = useState([]);
  const logExpress = (line) => setExpressLog((l) => [...l.slice(-7), `${new Date().toLocaleTimeString()} ${line}`]);
  const applyAvailability = (avail, source) => {
    if (avail === undefined || avail === null) { logExpress(`${source}: availability unknown`); return false; }
    const list = Object.keys(avail || {}).filter((k) => avail[k]);
    setExpressMethods(list);
    logExpress(`${source}: ${list.join(',') || 'NO wallets'}`);
    track('express_checkout_ready', { methods: list.join(',') || 'none', source, mode: 'embedded' });
    return true;
  };
  const onExpressReady = (ev) => { applyAvailability(ev?.availablePaymentMethods, 'ready'); };
  // Per Stripe docs an undefined `paymentMethods` on this event means "no buttons will show".
  const onExpressAvailable = (ev) => { applyAvailability(ev?.paymentMethods || {}, 'change'); };
  const onExpressFail = (err) => {
    const msg = String(err?.error?.message || err?.message || err || '').slice(0, 200);
    setExpressMethods([]);
    logExpress(`FAILED: ${msg}`);
    track('express_checkout_failed', { message: msg });
  };
  useEffect(() => {
    // Safety valve: if Stripe never reports availability, fall back to the wallet tabs inside the card form.
    if (!checkout || expressMethods !== null) return undefined;
    const t = setTimeout(() => { setExpressMethods((m) => (m === null ? [] : m)); logExpress('timeout: no answer in 8s'); track('express_checkout_timeout', { mode: 'embedded' }); }, 8000);
    return () => clearTimeout(t);
  }, [checkout, expressMethods]);
  const nudgeConsent = () => {
    setPayErr(cfg.consent_required_text || 'Please tick the refund policy box to continue.');
    track('express_consent_gate', { mode: 'embedded' });
    document.querySelector('[data-testid="mobile-consent-label"], [data-testid="consent-label"]')?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  };
  const walletsInCardForm = Array.isArray(expressMethods) && expressMethods.length === 0;

  const runConfirm = async (confirmOpts, method) => {
    if (!checkout) return;
    if (!consent) { setPayErr(cfg.consent_required_text || 'Please tick the refund policy box to continue.'); return; }
    setPaying(true); setPayErr('');
    const value = quote ? quote.total / quote.divisor : 0;
    track('checkout_pay_click', { value, currency: quote?.currency, code: quote?.code || '', mode: 'embedded', method });
    track('add_payment_info', { value, currency: quote?.currency, coupon: quote?.code || '', method });
    let r;
    try { r = await checkout.confirm(confirmOpts); } catch (e) { r = { type: 'error', error: { message: e?.message || 'Payment could not be started.' } }; }
    if (r && r.type === 'error') {
      setPayErr(r.error?.message || 'Payment failed. Please check your card details and try again.');
      setPaying(false);
      track('checkout_payment_error', { message: r.error?.message || '', method });
      document.querySelector('[data-testid="payment-panel"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    // Stripe normally redirects to return_url itself; if it resolved without redirecting, go there ourselves.
    if (r && r.type === 'success') window.location.assign(`/subscription?session_id=${encodeURIComponent(sessionId)}`);
  };
  const pay = () => runConfirm(undefined, 'card');
  const onExpressConfirm = (ev) => runConfirm({ expressCheckoutConfirmEvent: ev }, ev?.expressPaymentType || 'express');

  if (state.type === 'error') {
    return (
      <div className="max-w-lg mx-auto rounded-3xl bg-white border border-rose-200 p-6 text-center" data-testid="embedded-error">
        <AlertTriangle size={28} className="mx-auto text-rose-500 mb-2" />
        <p className="font-bold text-slate-900">We couldn't load the secure payment form.</p>
        <p className="text-sm text-slate-500 mt-1">{state.error?.message}</p>
        <div className="flex gap-2 justify-center mt-4">
          <button type="button" onClick={() => window.location.reload()} className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 text-white text-sm font-semibold px-4 py-2.5" data-testid="embedded-error-reload"><RefreshCw size={14} />Reload</button>
          <button type="button" onClick={() => onFallback('client_error')} className="rounded-xl border border-slate-300 text-sm font-semibold px-4 py-2.5 text-slate-700" data-testid="embedded-error-fallback">Pay on Stripe's page instead</button>
        </div>
      </div>
    );
  }

  const expired = checkout?.status?.type === 'expired';
  const shownErr = payErr || (expired ? 'This checkout session has expired — reload the page to start again.' : '') || checkout?.lastPaymentError?.message || '';

  return (
    <>
      <CheckoutColumns
        cfg={cfg}
        quote={quote}
        summary={{ onOpenCoupons: () => setDrawerOpen(true), onApplyCode: applyCode, onRemoveCode: removeCode, busyCode }}
        payment={(
          <PaymentPanel cfg={cfg} quote={quote} email={checkout?.email || email} consent={consent} onConsent={onConsent}
            onPay={pay} paying={paying} payError={shownErr} canPay={!!checkout?.canConfirm && !expired}>
            <div className="min-h-[220px]">
              {!checkout && <FieldsSkeleton />}
              {checkout && !walletsInCardForm && (
                <CheckoutErrorBoundary onCrash={onExpressFail} fallback={null}>
                  <ExpressRow cfg={cfg} methods={expressMethods} consent={consent} onReady={onExpressReady} onAvailable={onExpressAvailable}
                    onConfirm={onExpressConfirm} onLoadError={onExpressFail} onGate={nudgeConsent} />
                </CheckoutErrorBoundary>
              )}
              <PaymentElement key={walletsInCardForm ? 'pe-wallets' : 'pe-card'} options={walletsInCardForm ? PE_WITH_WALLETS : PE_CARD_ONLY} />
              {expressWanted && (
                <pre className="mt-3 rounded-lg bg-slate-900 text-emerald-300 text-[10px] leading-relaxed p-2 overflow-x-auto" data-testid="express-debug">
                  {`express debug (?express=1)\nstate: ${expressMethods === null ? 'waiting' : expressMethods.length ? expressMethods.join(',') : 'none → wallet tabs'}\n${expressLog.join('\n') || 'no events yet'}`}
                </pre>
              )}
            </div>
          </PaymentPanel>
        )}
      />
      <CouponDrawer open={drawerOpen} cfg={cfg} onClose={() => setDrawerOpen(false)} onApply={applyCode} busyCode={busyCode} error={couponErr} appliedCode={quote?.code} />
      <CouponAppliedModal cfg={cfg} data={applied} onClose={() => setApplied(null)} />
      <MobilePayBar cfg={cfg} quote={quote} onPay={pay} paying={paying} disabled={!checkout?.canConfirm || expired} consent={consent} onConsent={onConsent} />
    </>
  );
}

/**
 * Embedded mode: creates a Checkout Session (ui_mode=elements) server-side,
 * then mounts Stripe's Payment Element inside our page.
 */
export default function EmbeddedCheckout({ cfg, urlCode, email, onFallback }) {
  const [session, setSession] = useState(null);
  const [stripe, setStripe] = useState(null);
  const fallbackRef = useRef(onFallback);
  fallbackRef.current = onFallback;
  const pk = cfg.stripe_publishable_key;

  useEffect(() => {
    if (!isPublishable(pk)) { fallbackRef.current('bad_publishable_key'); return undefined; }
    let alive = true;
    getStripe(pk)
      .then((s) => { if (!alive) return; if (s) setStripe(s); else fallbackRef.current('stripe_js_unavailable'); })
      .catch(() => { if (alive) fallbackRef.current('stripe_js_failed'); });
    return () => { alive = false; };
  }, [pk]);

  useEffect(() => {
    if (!isPublishable(pk)) return undefined;
    let alive = true;
    api.post('/api/payments/checkout-session', { origin_url: window.location.origin, plan_id: cfg.plan === 'trial' ? 'trial' : 'pro' })
      .then((r) => {
        if (!alive) return;
        if (r.data?.embedded && r.data.client_secret) setSession(r.data);
        else fallbackRef.current(r.data?.reason || 'unavailable');
      })
      .catch(() => { if (alive) fallbackRef.current('request_failed'); });
    return () => { alive = false; };
  }, [pk, cfg.plan]);

  const options = useMemo(() => (session ? { clientSecret: session.client_secret, elementsOptions: { appearance: APPEARANCE, fonts: FONTS }, adaptivePricing: { allowed: false } } : null), [session]);

  if (!session || !stripe) {
    return (
      <CheckoutColumns cfg={cfg} quote={null} summary={{}}
        payment={<PaymentPanel cfg={cfg} quote={null} email={email} consent={false} onConsent={() => {}} onPay={() => {}} canPay={false}><FieldsSkeleton /></PaymentPanel>} />
    );
  }

  return (
    <CheckoutElementsProvider stripe={stripe} options={options}>
      <EmbeddedBody cfg={cfg} urlCode={urlCode} email={email} sessionId={session.session_id} onFallback={onFallback} />
    </CheckoutElementsProvider>
  );
}
